import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, ExternalLink, Download } from 'lucide-react';
import { inrFull, getStatutoryEvidenceFile } from '../data';
import { exportStatutoryMatrix } from '../exportStatutory';
import { headTotals } from '../statutoryTotals';
import type {
	StatutoryMatrix,
	StatutoryMatrixHead,
	StatutoryMatrixMonth,
	StatutoryZohoEntry,
} from '../types';

/**
 * The four-leg view — challan, return, bank, Zoho — in the shape Shamsuddin asked for on
 * 3-Sep-2026: "particular | as per zoho | as per return | diff", a month a row.
 *
 * TWO TABLES PER TAB, AND THEY ANSWER DIFFERENT QUESTIONS.
 *
 * The first is the DOCUMENT table: for each wage month, do we hold the challan, the return, and
 * a matching debit in the bank? It says nothing about the books. A month can be fully documented
 * and still be booked wrong.
 *
 * The second set is the LEDGER tables, one per head. PF is three Zoho accounts, not one — the
 * payable, the employer's contribution, and EDLI + admin — and each is matched against a
 * different part of the challan. Rolling them into a single "PF" row compares the full
 * remittance against the employee-deduction ledger and reports a clean year as twelve failures.
 *
 * No arithmetic on money happens here. Every rupee shown is a rupee the API computed; a number
 * derived twice is a number that eventually disagrees with itself.
 */

const CELL: Record<string, { label: string; cls: string; title: string }> = {
	tied: {
		label: 'tied',
		cls: 'bg-green-50 text-green-800 border-green-200',
		title: 'The books and the document agree',
	},
	differs: {
		label: 'differs',
		cls: 'bg-orange-50 text-orange-800 border-orange-200',
		title: 'Both sides exist but the amounts do not agree',
	},
	not_in_zoho: {
		label: 'not booked',
		cls: 'bg-red-50 text-red-800 border-red-200',
		title: 'The document says an amount is due or paid, and nothing was booked in Zoho',
	},
	no_document: {
		label: 'no document',
		cls: 'bg-sky-50 text-sky-800 border-sky-200',
		title: 'Zoho has an entry but we hold no challan or return to check it against',
	},
	no_activity: {
		label: '—',
		cls: 'bg-white text-gray-300 border-gray-100',
		title: 'Nothing on either side for this month',
	},
};

/**
 * How firmly a bank row is tied to a challan. `reference` means the department's own number was
 * printed in the bank remark — that is proof. The other two are amount-plus-date-window, which is
 * good evidence and not the same claim.
 *
 * This used to be a coloured chip in the table. It is now the tooltip on the statement link: the
 * strength matters when somebody questions a row, not on every row all the time, and a column that
 * carries both a number and two chips stops being readable.
 */
const BANK_MATCH: Record<string, string> = {
	reference: "Matched by reference — the bank remark contains the department's own challan number. This is proof of payment.",
	amount_window:
		'Matched by amount. PT is paid through a biller, so the remark names Karnataka but not the challan. Exact amount inside the due window.',
	return_amount:
		'Matched by amount. No challan on file for this month — matched on the amount declared in the return, against a bank remark naming the department.',
	via_employee:
		"Paid from an employee's own account. The company transferred the amount to the person (bank remark 'For PF') and the department's receipt names another bank. Accepted only because the transfer closes the challan to the rupee after any direct payment on the challan's own reference.",
};

/**
 * Open a stored document.
 *
 * Fetched through the axios service as a blob, NOT linked with a plain href. The proxy is
 * auth-gated and a new tab carries no Authorization header, so a direct link 401s — which looks
 * to the reader like "the challan is missing" when it is only unauthenticated. Same reason the
 * findings drawer fetches its evidence this way.
 */
const openDocument = async (storageKey: string) => {
	const url = await getStatutoryEvidenceFile(storageKey);
	window.open(url, '_blank', 'noopener');
};

/** A Zoho voucher number, clickable straight into Zoho Books. */
const ZohoChip: React.FC<{ e: StatutoryZohoEntry }> = ({ e }) => {
	const label = e.zoho_label ?? 'entry';
	const title = [e.description, e.contact, e.offset_account && `contra: ${e.offset_account}`, e.date]
		.filter(Boolean)
		.join('\n');
	return e.zoho_url ? (
		<a
			href={e.zoho_url}
			target='_blank'
			rel='noopener noreferrer'
			title={title}
			className='inline-flex items-center gap-0.5 rounded border border-blue-200 bg-blue-50 px-1.5 text-[11px] text-blue-700 hover:bg-blue-100'>
			{label}
			<ExternalLink className='w-2.5 h-2.5' />
		</a>
	) : (
		<span title={title} className='inline-block rounded border border-gray-200 bg-gray-50 px-1.5 text-[11px] text-gray-600'>
			{label}
		</span>
	);
};

const MONTH_NAMES = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2025-04" → "April 2025". A wage month is read aloud in meetings; the ISO form is not. */
const monthName = (ym: string) => {
	const [y, m] = ym.split('-');
	return `${MONTH_NAMES[Number(m) - 1] ?? m} ${y}`;
};

/** ["a","b","c"] → "a, b and c" */
const joinWords = (xs: string[]) =>
	xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

const Num: React.FC<{ v: number | null | undefined; muted?: boolean }> = ({ v, muted }) =>
	v === null || v === undefined ? (
		<span className='text-gray-300'>—</span>
	) : (
		<span className={muted ? 'text-gray-500' : ''}>{inrFull(v)}</span>
	);

/**
 * A number that opens the document it came from.
 *
 * The evidence used to live in its own "Proof" column of chips, which pushed the table past the
 * width of the card and put the link two columns away from the figure it proves. Hanging the link
 * on the figure removes a column and removes the question "which challan is this row's challan".
 */
const DocLink: React.FC<{ storageKey: string | null; title: string; children: React.ReactNode }> = ({
	storageKey,
	title,
	children,
}) =>
	storageKey ? (
		<button
			onClick={() => openDocument(storageKey)}
			title={title}
			className='text-blue-700 underline decoration-blue-300 underline-offset-2 hover:decoration-blue-700 tabular-nums'>
			{children}
		</button>
	) : (
		// No file uploaded for this row. Plain black, not a dead blue link — and the tooltip says
		// why, because the old Proof column printed a visible "—" here and that signal must not be
		// lost. It matters: PT has no challan PDF for any month, ESIC for nine of eleven.
		<span title='No document uploaded for this row yet'>{children}</span>
	);

/** month view — do we hold the paperwork for this month at all? */
const DocumentTable: React.FC<{ months: StatutoryMatrixMonth[] }> = ({ months }) => (
	// w-max, not w-full: with w-full the browser squeezes columns to fit and the wrapper never
	// scrolls, so a wide row just gets crushed. w-max lets the table take its natural width and
	// hands the overflow to the scroller; min-w-full keeps it filling the card when it is narrow.
	<div className='overflow-x-auto border border-gray-200 rounded-lg'>
		<table className='w-max min-w-full text-sm'>
			<thead className='bg-gray-50 text-gray-500'>
				<tr>
					<th className='text-left px-3 py-2 font-medium'>Wage month</th>
					<th className='text-right px-3 py-2 font-medium' title='Total on the challan. Click the amount to open the challan PDF.'>
						Challan
					</th>
					<th className='text-right px-3 py-2 font-medium' title='Total declared in the filed return (ECR for PF). An ECR declares contributions only, so it always sits below the challan by exactly EDLI + admin charges.'>
						Return
					</th>
					<th className='text-right px-3 py-2 font-medium'>Left the bank</th>
					<th className='text-left px-3 py-2 font-medium'>Paid on</th>
					<th className='text-right px-3 py-2 font-medium' title='PF deducted in our own payroll register. The challan also covers contractor site labour, who are not in this file.'>
						In our payroll
					</th>
					<th className='text-left px-3 py-2 font-medium'>Reference</th>
				</tr>
			</thead>
			<tbody className='divide-y divide-gray-100'>
				{months.map(m => {
					const noBank = m.bank_rs === null;
					const noChallan = m.challan_rs === null;
					return (
						<tr key={m.wage_month} className={noBank || noChallan ? 'bg-red-50/30' : 'hover:bg-gray-50'}>
							{/* "(14)", not "14 people". Same line, a third of the width, and the word is not
							    doing any work — the tooltip carries what the number counts. */}
							<td className='px-3 py-2 whitespace-nowrap text-gray-800'>
								{m.wage_month}
								{!!m.members && (
									<span title={`${m.members} members on the challan for this month`} className='text-xs text-gray-400 ml-1'>
										({m.members})
									</span>
								)}
							</td>
							<td className='px-3 py-2 text-right tabular-nums whitespace-nowrap align-top'>
								{m.challan_rs === null ? (
									<span className='text-gray-300'>—</span>
								) : (
									<DocLink
										storageKey={m.challan_document?.storage_key ?? null}
										title={m.challan_document?.storage_key ? `Open ${m.challan_document.document_title ?? 'the challan'}` : ''}>
										{inrFull(m.challan_rs)}
									</DocLink>
								)}
								{/* A month with a main and a supplementary challan: the amount opens the main,
								    "(+1)" opens the other. Two is the ceiling by design (ADR 0027). */}
								{m.challan_documents?.slice(1).map(d => (
									<span key={d.storage_key ?? d.document_title ?? ''} className='ml-1 text-[11px]'>
										<DocLink storageKey={d.storage_key} title={`Open ${d.document_title ?? 'the second challan'}`}>
											(+1)
										</DocLink>
									</span>
								))}
							</td>
							{/* The return is ALWAYS below the challan, by exactly EDLI + admin — an ECR
							    declares contributions, not levies. The reason lives in the tooltip now; a
							    chip on every row said the same thing twelve times. A month with no ECR still
							    shows what its return should total when it arrives.

							    A FILED figure is a link to the return PDF; a DERIVED one never is. That is
							    the whole distinction this column has to carry, and a link is the cheapest
							    way to carry it — you cannot open a document that does not exist. */}
							<td className='px-3 py-2 text-right tabular-nums whitespace-nowrap align-top'>
								{m.return_rs !== null ? (
									<DocLink
										storageKey={m.return_document?.storage_key ?? null}
										title={
											m.return_gap_is_charges
												? `Open ${m.return_document?.document_title ?? 'the filed return'}. Below the challan by ${inrFull(Math.abs(m.return_vs_challan_rs ?? 0))}, which is exactly EDLI + admin charges (A/c 2 + A/c 21 + A/c 22). An ECR declares contributions only — charges are not part of it. Not under-declaring.`
												: `Open ${m.return_document?.document_title ?? 'the filed return'}`
										}>
										{inrFull(m.return_rs)}
									</DocLink>
								) : m.return_expected_rs !== null ? (
									<span
										title='The ECR for this month is not loaded. This is what it should total: challan minus EDLI and admin charges. Check the ECR against it when it arrives.'
										className='text-gray-400 italic'>
										expect {inrFull(m.return_expected_rs)}
									</span>
								) : (
									<span className='text-gray-300'>—</span>
								)}
							</td>
							<td className='px-3 py-2 text-right tabular-nums whitespace-nowrap align-top'>
								<Num v={m.bank_rs} />
								{/* The one weak match that is shown, not just tooltipped: money that went to a
								    person before it went to the department deserves a reader's second look. */}
								{m.bank_match_type === 'via_employee' && (
									<span
										title={BANK_MATCH.via_employee}
										className='ml-2 inline-block rounded border border-amber-200 bg-amber-50 px-1.5 text-[11px] text-amber-800 align-middle'>
										via employee
									</span>
								)}
								{noBank && !noChallan && (
									<span
										title='A challan exists for this month but no payment matching it appears in any loaded bank account.'
										className='ml-2 inline-block rounded border border-red-200 bg-red-50 px-1.5 text-[11px] text-red-700 align-middle'>
										not in bank
									</span>
								)}
								{/* Just "(p.26)" after the amount. The words "bnk stmt" said nothing the column
								    header does not already say, and the page number is the only part anyone
								    acts on — it is where in a 91-page PDF the row is printed. */}
								{m.bank_evidence?.map(b =>
									b.storage_key ? (
										<span key={b.bank_txn_id} className='ml-1 text-[11px]'>
											<DocLink
												storageKey={b.storage_key}
												title={[
													BANK_MATCH[b.match_type ?? m.bank_match_type ?? ''],
													b.statement,
													b.remarks,
												]
													.filter(Boolean)
													.join('\n')}>
												({b.page ? `p.${b.page}` : 'open'})
											</DocLink>
										</span>
									) : null
								)}
							</td>
							<td className='px-3 py-2 text-gray-600 whitespace-nowrap align-top'>
								{m.bank_date ?? m.paid_on ?? '—'}
							</td>
							{/* The fifth leg. Shown next to the challan, never netted against it — our own
							    register and the challan cover different populations, and subtracting one from
							    the other would invent a shortfall that is really a scope difference. */}
							<td className='px-3 py-2 text-right tabular-nums whitespace-nowrap align-top'>
								{m.payroll ? (
									<>
										<Num v={m.payroll.pf_employee_rs} muted />
										{m.payroll.not_in_payroll_rs != null && m.payroll.not_in_payroll_rs > 1 && (
											<span
												title={`Challan employee share ${inrFull((m.payroll.pf_employee_rs ?? 0) + m.payroll.not_in_payroll_rs)} minus our payroll register ${inrFull(m.payroll.pf_employee_rs ?? 0)} = ${inrFull(m.payroll.not_in_payroll_rs)} for people NOT in that register — contractor site labour. Source: ${m.payroll.source_file}`}
												className='ml-2 inline-block rounded border border-amber-200 bg-amber-50 px-1.5 text-[11px] text-amber-700 align-middle'>
												+{inrFull(m.payroll.not_in_payroll_rs)} site
											</span>
										)}
									</>
								) : (
									<span className='text-gray-300'>—</span>
								)}
							</td>
							<td className='px-3 py-2 text-gray-500 font-mono text-xs align-top'>{m.reference ?? '—'}</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	</div>
);

/**
 * A signed difference.
 *
 * Sign carries the whole meaning and must not be thrown away by the styling. Booking MORE than the
 * challan and booking LESS are opposite problems — one is an over-provision sitting in a liability,
 * the other is an under-provision — and painting both red said "wrong" without saying which way.
 * Green is over, red is under, grey is nil.
 */
const Diff: React.FC<{ v: number | null; bold?: boolean }> = ({ v, bold }) => {
	if (v === null || v === undefined) return <span className='text-gray-300'>—</span>;
	const nil = Math.abs(v) <= 1;
	const cls = nil ? 'text-gray-400' : v > 0 ? 'text-green-700' : 'text-red-700';
	return (
		<span className={`${cls} ${bold ? 'font-semibold' : 'font-medium'}`}>
			{nil ? inrFull(0) : `${v > 0 ? '+' : '−'}${inrFull(Math.abs(v))}`}
		</span>
	);
};

/** one Zoho account, month by month — exactly the table the mail sketched. */
const HeadTable: React.FC<{
	head: StatutoryMatrixHead;
	line: string;
	months: StatutoryMatrixMonth[];
}> = ({ head, line, months }) => {
	// The ESIC challan prints one figure with no split, so the employer head has no challan column
	// at all and is measured against the return. Naming the column after whichever source actually
	// filled it keeps the header honest instead of showing "As per challan" over return figures.
	const fromChallan = head.rows.some(r => r.challan_rs !== null);
	const expectedLabel = fromChallan ? 'As per challan' : 'As per return';

	/**
	 * Running total of the differences — the answer to "do these cancel out?".
	 *
	 * A single month being over or under usually means timing: one payroll journal covering two
	 * months, or a provision posted a day into the next month. Those wash out, and the running
	 * total walks back to zero. What does NOT wash out is a real shortfall, and that shows as a
	 * running total which never returns to nil. Reading month-by-month differences alone cannot
	 * tell those two apart, which is why this column exists next to them.
	 */
	let carry = 0;
	const running = head.rows.map(r => {
		carry += r.diff_rs ?? 0;
		return carry;
	});

	// Totals over months we hold a document for. Not the API's column sums — see statutoryTotals.ts.
	const t = headTotals(head);
	const net = t.netRs;
	return (
		<section>
			<div className='flex flex-wrap items-baseline justify-between gap-2 mb-1.5'>
				<h4 className='text-sm font-medium text-gray-800'>
					{head.label}
					<span className='ml-2 text-xs font-normal text-gray-400'>
						Zoho “{head.zoho_account}” · {head.zoho_side === 'credit' ? 'credit' : 'debit'} ·{' '}
						{head.align === 'wage_month' ? 'same month' : 'month after'}
					</span>
				</h4>
				<span
					className={`text-xs px-2 py-0.5 rounded-full border ${
						head.totals.open_months === 0
							? 'bg-green-50 text-green-700 border-green-200'
							: 'bg-amber-50 text-amber-700 border-amber-200'
					}`}>
					{head.totals.tied_months} of 12 tie
				</span>
			</div>
			<p className='text-xs text-gray-500 mb-2 flex items-start gap-1'>
				<HelpCircle className='w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400' />
				{head.question}
			</p>
			<div className='overflow-x-auto border border-gray-200 rounded-lg'>
				<table className='w-full text-sm'>
					<thead className='bg-gray-50 text-gray-500'>
						<tr>
							<th className='text-left px-3 py-2 font-medium'>Month</th>
							<th className='text-right px-3 py-2 font-medium'>{expectedLabel}</th>
							<th className='text-right px-3 py-2 font-medium'>As per Zoho</th>
							<th className='text-right px-3 py-2 font-medium'>Difference</th>
							<th className='text-right px-3 py-2 font-medium' title='The differences added up as you go down. If it walks back to zero the earlier gaps were only timing.'>
								Running
							</th>
							<th className='text-left px-3 py-2 font-medium'>Status</th>
							<th className='text-left px-3 py-2 font-medium'>Zoho voucher</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-100'>
						{head.rows.map((r, i) => {
							const v = CELL[r.status] ?? CELL.no_activity;
							const expected = r.challan_rs ?? r.return_rs;
							return (
								<tr key={r.wage_month} className='hover:bg-gray-50'>
									<td className='px-3 py-2 whitespace-nowrap text-gray-800'>
										{r.wage_month}
										{r.zoho_month !== r.wage_month && (
											<span className='text-xs text-gray-400 ml-2'>Zoho {r.zoho_month}</span>
										)}
									</td>
									<td className='px-3 py-2 text-right tabular-nums'>
										<Num v={expected} />
									</td>
									<td className='px-3 py-2 text-right tabular-nums'>
										<Num v={r.zoho_rs} />
									</td>
									<td className='px-3 py-2 text-right tabular-nums'>
										<Diff v={r.diff_rs} />
									</td>
									<td className='px-3 py-2 text-right tabular-nums'>
										<Diff v={running[i]} />
									</td>
									<td className='px-3 py-2'>
										<span
											title={v.title}
											className={`inline-block rounded-md border px-2 py-0.5 text-xs ${v.cls}`}>
											{v.label}
										</span>
									</td>
									{/* The vouchers that make the Zoho figure. This is what accounts asked for:
									    a number they can take into Zoho and open, instead of a total to trust. */}
									<td className='px-3 py-2'>
										<div className='flex flex-wrap gap-1'>
											{r.zoho_entries?.length ? (
												r.zoho_entries.map(e => (
													<ZohoChip key={`${e.zoho_transaction_id}-${e.amount_rs}`} e={e} />
												))
											) : (
												<span className='text-gray-300'>—</span>
											)}
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
					<tfoot className='bg-gray-50 text-gray-700 font-medium'>
						<tr>
							<td className='px-3 py-2'>
								Total
								<span className='block text-[11px] font-normal text-gray-500'>
									{t.comparableMonths} month{t.comparableMonths === 1 ? '' : 's'} we hold a document for
								</span>
							</td>
							<td className='px-3 py-2 text-right tabular-nums'>{inrFull(t.documentRs)}</td>
							<td className='px-3 py-2 text-right tabular-nums'>{inrFull(t.zohoRs)}</td>
							<td className='px-3 py-2 text-right tabular-nums'>
								<Diff v={net} bold />
							</td>
							<td className='px-3 py-2 text-right tabular-nums'>
								<Diff v={net} bold />
							</td>
							<td colSpan={2} />
						</tr>
					</tfoot>
				</table>
			</div>
			{/* The verdict in words. A signed number at the foot of a table is easy to read the wrong
			    way round, and this is the sentence somebody will repeat in a meeting. */}
			{/* Money left the bank, and the books do not know about it.

			    This is the sharpest kind of break on this table and it is NOT the same as "not
			    booked". Three months here show nothing in Zoho, but for April and May we cannot show
			    the money leaving either — those are a question for accounts, not a posting error.
			    June is different: the bank proves ₹1,26,579 went to EPFO and Zoho has no entry at
			    all. That is a missing voucher and someone can fix it today.

			    Scoped to the payment head on purpose. A provision table has no bank side, so the
			    same sentence there would be comparing against nothing. */}
			{head.key === 'PF_PAYMENT' &&
				(() => {
					const paidNotBooked = head.rows.filter(r => {
						const m = months.find(x => x.wage_month === r.wage_month);
						return m && m.bank_rs !== null && !r.zoho_rs;
					});
					if (!paidNotBooked.length) return null;
					const amounts = paidNotBooked.map(r => {
						const m = months.find(x => x.wage_month === r.wage_month);
						return inrFull(m?.bank_rs as number);
					});
					return (
						<div className='flex items-start gap-2 mt-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2'>
							<AlertTriangle className='w-4 h-4 mt-0.5 shrink-0 text-red-500' />
							<span>
								We have bank proof that {joinWords(amounts)} left for{' '}
								{joinWords(paidNotBooked.map(r => monthName(r.wage_month)))} {line}, but Zoho has no
								expense entry recorded for {paidNotBooked.length === 1 ? 'that payment' : 'those payments'} —
								books need updating.
							</span>
						</div>
					);
				})()}
			{/* The verdict as one sentence somebody can act on: how many months disagree, which way
			    the year lands, and what to do about it. The paragraph below breaks the same figure
			    down; this is the line that gets repeated in a meeting, so it names the remedy. */}
			{head.totals.tied_months < head.rows.length && Math.abs(net) > 1 && (
				<div className='flex items-start gap-2 mt-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2'>
					<AlertTriangle className='w-4 h-4 mt-0.5 shrink-0 text-red-500' />
					<span>
						Zoho's {line} booking doesn't match the {fromChallan ? 'challan' : 'return'} for{' '}
						{head.rows.length - head.totals.tied_months} of {head.rows.length} months,{' '}
						{net < 0 ? 'under-recording' : 'over-recording'} our {line}{' '}
						{/* An expense account holds a COST, not a liability. Calling the employer's own
						    contribution a liability would send accounts looking on the wrong side of the
						    balance sheet for a figure that is sitting in P&L. */}
						{/expense/i.test(head.label) ? 'cost' : 'liability'} by{' '}
						<span className='font-semibold'>{inrFull(Math.abs(net))}</span> for the year — needs a
						correction entry.
					</span>
				</div>
			)}
			<div className='text-xs mt-1.5 space-y-1'>
				<p>
					{Math.abs(net) <= 1 ? (
						<span className='text-green-700'>
							Nets to zero across the year — every month's gap is cancelled by another, so these are
							timing differences, not a shortfall.
						</span>
					) : net > 0 ? (
						<span className='text-green-700'>
							Booked <span className='font-semibold'>{inrFull(net)} MORE</span> than the documents ask
							for, over the year. Does not net off.
						</span>
					) : (
						<span className='text-red-700'>
							Booked <span className='font-semibold'>{inrFull(Math.abs(net))} LESS</span> than the
							documents ask for, over the year. Does not net off.
						</span>
					)}
				</p>
				{/* A month with no paperwork is not evidence of anything, so it is named rather than
				    quietly folded into a total that would then flatter the result. */}
				{!!t.excludedMonths.length && (
					<p className='text-amber-700'>
						{t.excludedMonths.join(', ')} left out of the total — no challan or return on file, so
						there is nothing to compare the books against. Zoho booked{' '}
						<span className='font-semibold'>{inrFull(t.excludedZohoRs)}</span> in{' '}
						{t.excludedMonths.length === 1 ? 'that month' : 'those months'}. Get the document and
						this figure will move.
					</p>
				)}
				{!!t.notBookedMonths.length && (
					<p className='text-red-700'>
						{t.notBookedMonths.join(', ')} — the document says{' '}
						<span className='font-semibold'>{inrFull(t.notBookedRs)}</span> was due and{' '}
						<span className='font-semibold'>nothing at all</span> was booked. Included in the total
						above, because not booking a cost is the finding, not a reason to exclude it.
					</p>
				)}
			</div>
		</section>
	);
};

const GAP_LABEL: Record<string, string> = {
	challan_missing: 'Challan / receipt not on file',
	no_bank_payment: 'No matching payment in the bank',
	return_missing: 'Filed return not loaded',
};

export const StatutoryMatrixView: React.FC<{ matrix: StatutoryMatrix }> = ({ matrix }) => {
	const [tab, setTab] = useState<string>(matrix.lines[0]?.line ?? 'PF');
	const line = matrix.lines.find(l => l.line === tab);
	const gaps = matrix.gaps.filter(g => (tab === 'GAPS' ? true : g.line === tab));

	return (
		<div className='space-y-5'>
			<div className='flex flex-wrap gap-1 border-b border-gray-200'>
				{matrix.lines.map(l => (
					<button
						key={l.line}
						onClick={() => setTab(l.line)}
						className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
							tab === l.line
								? 'border-blue-600 text-blue-700'
								: 'border-transparent text-gray-500 hover:text-gray-800'
						}`}>
						{l.label}
					</button>
				))}
				<button
					onClick={() => setTab('GAPS')}
					className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
						tab === 'GAPS'
							? 'border-blue-600 text-blue-700'
							: 'border-transparent text-gray-500 hover:text-gray-800'
					}`}>
					What is missing
					{!!matrix.gaps.length && (
						<span className='ml-2 rounded-full bg-red-100 text-red-700 px-1.5 text-xs'>
							{matrix.gaps.length}
						</span>
					)}
				</button>
				{/* Exports the WHOLE workbook, not the visible tab. Somebody who wants this in Excel
				    wants to work the year, and re-exporting once per tab is three chances to end up
				    with three files that disagree. */}
				<button
					onClick={() => exportStatutoryMatrix(matrix)}
					className='ml-auto mb-1 inline-flex items-center gap-1.5 self-center rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:border-gray-400'>
					<Download className='w-3.5 h-3.5' /> Export to Excel
				</button>
			</div>

			{tab === 'GAPS' ? (
				<section className='space-y-2'>
					<p className='text-xs text-gray-500'>
						A missing document is not the same thing as a difference. A month can tie to the rupee and
						still have no challan on file — that is an audit problem even though no number disagrees.
					</p>
					{gaps.length === 0 ? (
						<div className='flex items-center gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2'>
							<CheckCircle2 className='w-4 h-4' /> Every document is on file.
						</div>
					) : (
						<div className='overflow-x-auto border border-gray-200 rounded-lg'>
							<table className='w-full text-sm'>
								<thead className='bg-gray-50 text-gray-500'>
									<tr>
										<th className='text-left px-3 py-2 font-medium'>Head</th>
										<th className='text-left px-3 py-2 font-medium'>Month</th>
										<th className='text-left px-3 py-2 font-medium'>What is missing</th>
										<th className='text-right px-3 py-2 font-medium'>Amount</th>
										<th className='text-left px-3 py-2 font-medium'>Detail</th>
									</tr>
								</thead>
								<tbody className='divide-y divide-gray-100'>
									{gaps.map((g, i) => (
										<tr key={`${g.line}-${g.wage_month}-${g.kind}-${i}`} className='hover:bg-gray-50'>
											<td className='px-3 py-2 font-medium text-gray-800'>{g.line}</td>
											<td className='px-3 py-2 whitespace-nowrap text-gray-700'>{g.wage_month}</td>
											<td className='px-3 py-2'>
												<span
													className={`inline-block rounded-md border px-2 py-0.5 text-xs ${
														g.kind === 'no_bank_payment'
															? 'bg-red-50 text-red-800 border-red-200'
															: 'bg-amber-50 text-amber-800 border-amber-200'
													}`}>
													{GAP_LABEL[g.kind] ?? g.kind}
												</span>
											</td>
											<td className='px-3 py-2 text-right tabular-nums'>
												<Num v={g.amount_rs} />
											</td>
											<td className='px-3 py-2 text-gray-600'>{g.detail}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</section>
			) : (
				line && (
					<div className='space-y-6'>
						<section>
							<div className='flex items-baseline justify-between mb-2'>
								<h3 className='text-sm font-medium text-gray-700'>
									Documents held — {line.label}
								</h3>
								<span className='text-xs text-gray-400'>
									row = wage month · dues are paid the following month
								</span>
							</div>
							<DocumentTable months={line.months} />
							{/* A challan with no matching bank debit. This is the one gap on this table that
							    is about MONEY rather than paperwork — the department billed us and we cannot
							    show the payment leaving — so it is stated in words rather than left as a
							    chip somebody has to notice. Months and amounts come from the data, so the
							    sentence cannot go stale once a statement is loaded. */}
							{(() => {
								const unpaid = line.months.filter(m => m.challan_rs !== null && m.bank_rs === null);
								if (!unpaid.length) return null;
								return (
									<div className='flex items-start gap-2 mt-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2'>
										<AlertTriangle className='w-4 h-4 mt-0.5 shrink-0 text-red-500' />
										<span>
											We have no bank proof that {line.label} for{' '}
											{joinWords(unpaid.map(m => monthName(m.wage_month)))} (
											{joinWords(unpaid.map(m => inrFull(m.challan_rs as number)))}) was actually
											paid — need confirmation from accounts.
										</span>
									</div>
								);
							})()}
							{line.months.some(m => (m.interest_7q_rs ?? 0) > 0 || (m.damages_14b_rs ?? 0) > 0) && (
								<div className='flex items-start gap-2 mt-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2'>
									<AlertTriangle className='w-4 h-4 mt-0.5 shrink-0' />
									<span>
										A challan carries s.7Q interest or s.14B damages — the department has charged us
										for paying late.
									</span>
								</div>
							)}
						</section>

						<div className='space-y-6'>
							<h3 className='text-sm font-medium text-gray-700'>
								Books vs documents — one table per Zoho ledger
							</h3>
							{line.heads.map(h => (
								<HeadTable key={h.key} head={h} line={line.line} months={line.months} />
							))}
						</div>
					</div>
				)
			)}
		</div>
	);
};

export default StatutoryMatrixView;
